import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PostCard from "@/components/features/PostCard";
import SuggestionCard from "@/components/features/SuggestionCard";
import StoriesBar from "@/components/StoriesBar";
import "./PluseAds.css";
import PluseAdSense from "@/components/PluseAdSense";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import FileUpload from "@/components/features/FileUpload";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Image as ImageIcon,
  BarChart3,
  Megaphone,
  X,
  Plus,
  TrendingUp,
  Hash,
  Bookmark,
  ShieldAlert,
  ArrowLeft,
} from "lucide-react";


function PluseAdBox({ position = "feed" }) {
  const isSide = position === "side";

  return (
    <aside className={`pluse-ad-box ${isSide ? "pluse-side-ad" : "pluse-feed-ad"}`}>
      <PluseAdSense type={isSide ? "display" : "inFeed"} />
    </aside>
  );
}

export default function PlusePage() {
  const { user } = useAuth();
  const { id: pluseId } = useParams();
  const { tag: hashtagTag } = useParams();
  const navigate = useNavigate();


  const [posts, setPosts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [sugFreq, setSugFreq] = useState(4);
  const [trending, setTrending] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);

  // States: "icons" (mobile popover), "composer" (expanded full box), "saved" (saved view drawer/modal), or null (closed)
  const [mode, setMode] = useState(null);

  // Compact inline creator state
  const [content, setContent] = useState("");
  const [media, setMedia] = useState("");
  const [kind, setKind] = useState("text");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [posting, setPosting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const taRef = useRef(null);

  const load = async () => {
   

    try {
      const { data } = await api.get("/posts?limit=40");
      setPosts(data);
    } catch (e) {
      toast.error("Failed to load Pluse");
    }
    try {
      const { data } = await api.get("/pluse/suggestions?limit=8");
      if (data?.enabled) {
        setSuggestions(data.items || []);
        setSugFreq(Math.max(2, data.frequency || 4));
      } else {
        setSuggestions([]);
      }
    } catch {}
    try {
      const { data } = await api.get("/hashtags/trending?limit=10");
      setTrending(data || []);
    } catch {}
    if (user) {
      try {
        const { data } = await api.get("/posts/saved");
        setSavedPosts(data || []);
      } catch {}
    }
  };

  const visiblePosts = hashtagTag
    ? posts.filter((post) => {
        const tag = decodeURIComponent(hashtagTag).replace(/^#/, "").toLowerCase();
        const savedTags = Array.isArray(post.hashtags) ? post.hashtags : [];
        const content = post.content || "";
        return savedTags.some((item) =>
          String(typeof item === "string" ? item : item?.tag || "")
            .replace(/^#/, "").toLowerCase() === tag
        ) || new RegExp(`(^|[^A-Za-z0-9_])#${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![A-Za-z0-9_])`, "i").test(content);
      })
    : posts;

  useEffect(() => {
    load();
  }, [user?.id]);

  useEffect(() => {
    if (mentionQuery === null) {
      setMentionResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/mentions/suggest", {
          params: { q: mentionQuery },
        });
        setMentionResults(data);
      } catch {}
    }, 180);
    return () => clearTimeout(t);
  }, [mentionQuery]);

  const onContentChange = (e) => {
    const v = e.target.value;
    setContent(v);
    const m = v.slice(0, e.target.selectionStart).match(/@([A-Za-z0-9_]*)$/);
    setMentionQuery(m ? m[1] : null);
  };

  const pickMention = (uname) => {
    setContent((t) => t.replace(/@([A-Za-z0-9_]*)$/, `@${uname} `));
    setMentionQuery(null);
    taRef.current?.focus();
  };

  useEffect(() => {
    if (!pluseId || posts.length === 0) return;
    const target = posts.find((post) => String(post.id) === String(pluseId));
    if (!target) return;
    setTimeout(() => {
      const element = document.querySelector(
        `[data-testid="post-card-${target.id}"]`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  }, [pluseId, posts]);

  const reset = () => {
    setContent("");
    setMedia("");
    setKind("text");
    setPollOptions(["", ""]);
    if (mode === "composer") setMode(null);
  };

  const openWithKind = (selectedKind) => {
    setKind(selectedKind);
    setMode("composer");
    setTimeout(() => taRef.current?.focus(), 100);
  };

  const handleProfileClick = () => {
    if (mode === "composer" || mode === "icons") {
      setMode(null);
    } else {
      setMode("icons");
    }
  };

  const submit = async () => {
    if (!content.trim() && !media) {
      toast.error("Write something or upload an image first");
      return;
    }
    setPosting(true);
    try {
      const extractedTags = (content.match(/#[A-Za-z0-9_]+/g) || []).map((t) =>
        t.replace("#", "")
      );
      const payload = {
        kind,
        content,
        media_url: media || null,
        poll_options:
          kind === "poll" ? pollOptions.filter((o) => o.trim()) : null,
        tags: extractedTags,
      };
      if (
        kind === "poll" &&
        (!payload.poll_options || payload.poll_options.length < 2)
      ) {
        toast.error("Add at least 2 poll options");
        setPosting(false);
        return;
      }
      await api.post("/posts", payload);
      reset();
      toast.success("Posted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setPosting(false);
    }
  };



  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 md:pt-32 pb-6 md:pb-10"
      data-testid="feed-page"
    >
      <div className="pluse-side-ad-left">
        <PluseAdBox position="side" />
      </div>
      <div className="pluse-side-ad-right">
        <PluseAdBox position="side" />
      </div>
      {/* Top Header Swipeable / Drag-Enabled Navigation Tabs (Pluse & Communities) */}
      <div className="flex items-center justify-between mb-6">
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.08}
          onDragEnd={(e, info) => {
            if (info.offset.x > 40) {
              navigate("/pluse");
            } else if (info.offset.x < -40) {
              navigate("/Communities");
            }
          }}
          className="flex items-center gap-1.5 p-1 glass rounded-2xl cursor-grab active:cursor-grabbing touch-pan-x"
        >
          <a
            href="https://creators.lostdrkc.in/pluse/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/pluse");
            }}
            className="px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 text-black shadow-lg"
            style={{ background: "linear-gradient(135deg,#FFBF00,#FF8A00)" }}
          >
            Pluse
          </a>
          <a
            href="https://creators.lostdrkc.in/Communities"
            onClick={(e) => {
              e.preventDefault();
              navigate("/Communities");
            }}
            className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
          >
            Communities
          </a>
        </motion.div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
          DrkC Pluse
        </div>
      </div>

      <div className="mb-6">
        <h1 className="font-display font-black text-3xl md:text-4xl">
          DrkC <span className="gradient-text">pluse</span>
        </h1>
      </div>

      <div className="mt-12">
        <StoriesBar />
      </div>

      {/* Top Bar Container: Profile/Composer on left, Trending Hashtags positioned directly right next to it */}
      {user && (
        <div
          className="flex items-center gap-3 mb-6 relative flex-wrap sm:flex-nowrap"
          data-testid="feed-composer"
        >
          {/* Profile & Inline/Popup Icons + Expanding Composer Box with Spring Animation */}
          <div className="relative flex items-center gap-3 shrink-0">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="glass rounded-2xl p-2.5 shadow-md flex items-center gap-3 relative cursor-pointer"
            >
              <button
                onClick={handleProfileClick}
                className="flex items-center gap-2 p-0.5 rounded-full hover:opacity-85 transition-opacity"
                title="Toggle Actions"
              >
                <img
                  src={user.avatar_url || user.avatar}
                  alt=""
                  className="w-8 h-8 rounded-full border border-white/10 shrink-0 object-cover"
                />
              </button>

              {/* Desktop Always Visible Icons / Mobile Toggled Icons ("icons" mode) with hover spring effects */}
              <div
                className={`items-center gap-1.5 shrink-0 ${
                  mode === "icons" ? "flex" : "hidden sm:flex"
                }`}
              >
                <motion.button
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openWithKind("text")}
                  className="p-1.5 rounded-xl glass-hover text-white/80 hover:text-white transition-colors"
                  title="Text Post"
                >
                  <Plus className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openWithKind("image")}
                  className="p-1.5 rounded-xl glass-hover text-[#6EE7FF] hover:bg-[#6EE7FF]/10 transition-colors"
                  title="Image Post"
                >
                  <ImageIcon className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openWithKind("poll")}
                  className="p-1.5 rounded-xl glass-hover text-[#FF6AD5] hover:bg-[#FF6AD5]/10 transition-colors"
                  title="Create Poll"
                >
                  <BarChart3 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openWithKind("announcement")}
                  className="p-1.5 rounded-xl glass-hover text-[#FFD54A] hover:bg-[#FFD54A]/10 transition-colors"
                  title="Announcement"
                >
                  <Megaphone className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setMode(mode === "saved" ? null : "saved")}
                  className={`p-1.5 rounded-xl glass-hover transition-colors ${
                    mode === "saved"
                      ? "text-[#FFD54A] bg-[#FFD54A]/10"
                      : "text-white/80 hover:text-[#FFD54A]"
                  }`}
                  title="Saved Pluse"
                >
                  <Bookmark className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>

            {/* Expanded Full Composer Box Popover / Modal with Elastic Spring Animation */}
            <AnimatePresence>
              {mode === "composer" && (
                <motion.div
                  initial={{ opacity: 0, y: 15, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 15, scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 320, damping: 25 }}
                  className="absolute left-0 top-14 z-40 w-80 sm:w-96 glass-strong rounded-2xl border border-white/10 p-4 shadow-2xl backdrop-blur-xl"
                >
                  <div className="flex items-center justify-between pb-2 mb-3 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <img
                        src={user.avatar_url || user.avatar}
                        alt=""
                        className="w-6 h-6 rounded-full border border-white/10 object-cover"
                      />
                      <span className="text-xs uppercase tracking-widest text-white/70 font-semibold">
                        New {kind}
                      </span>
                    </div>
                    <button
                      onClick={() => setMode(null)}
                      className="text-white/50 hover:text-white p-1 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="relative">
                    <Textarea
                      ref={taRef}
                      value={content}
                      onChange={onContentChange}
                      placeholder={
                        kind === "poll"
                          ? "Ask the community…"
                          : "Share an update… #tags @mentions"
                      }
                      data-testid="feed-compose-textarea"
                      className="w-full min-h-[70px] max-h-36 bg-transparent border-0 focus-visible:ring-0 text-sm resize-none px-0 py-1"
                    />

                    {mentionResults.length > 0 && (
                      <div
                        className="absolute z-30 left-0 mt-1 w-full glass-strong rounded-xl border border-white/10 p-1 shadow-xl"
                        data-testid="pluse-mention-dropdown"
                      >
                        {mentionResults.map((u) => (
                          <button
                            key={u.username}
                            onClick={() => pickMention(u.username)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 text-left"
                          >
                            <img
                              src={u.avatar_url || undefined}
                              alt=""
                              className="w-5 h-5 rounded-full object-cover shrink-0"
                            />
                            <span className="text-xs truncate">
                              {u.name}{" "}
                              <span className="text-white/40">
                                @{u.username}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {kind === "image" && (
                    <div className="mb-3">
                      {media ? (
                        <div className="relative inline-block">
                          <img
                            src={media}
                            alt=""
                            className="max-h-36 rounded-xl w-full object-cover"
                          />
                          <button
                            onClick={() => setMedia("")}
                            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 border border-white/20 flex items-center justify-center"
                            data-testid="feed-clear-image"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <FileUpload
                          kind="image"
                          accept="image/*"
                          onUploaded={(r) => setMedia(r?.url || "")}
                          buttonLabel="Upload image"
                          testid="feed-image-upload"
                        />
                      )}
                    </div>
                  )}

                  {kind === "poll" && (
                    <div className="space-y-2 mb-3">
                      {pollOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const c = [...pollOptions];
                              c[i] = e.target.value;
                              setPollOptions(c);
                            }}
                            placeholder={`Option ${i + 1}`}
                            data-testid={`poll-option-input-${i}`}
                            className="bg-white/5 border-white/10 rounded-xl h-8 text-xs"
                          />
                          {pollOptions.length > 2 && (
                            <button
                              onClick={() =>
                                setPollOptions(
                                  pollOptions.filter((_, x) => x !== i)
                                )
                              }
                              className="text-white/50 hover:text-red-400 p-1.5 shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                      {pollOptions.length < 6 && (
                        <button
                          onClick={() => setPollOptions([...pollOptions, ""])}
                          className="text-[11px] text-white/60 hover:text-white flex items-center gap-1"
                          data-testid="add-poll-option"
                        >
                          <Plus className="w-3 h-3" /> Add option
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2.5 border-t border-white/10">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setKind("image")}
                        className={`p-1.5 rounded-full glass-hover ${
                          kind === "image"
                            ? "text-[#6EE7FF]"
                            : "text-white/60"
                        }`}
                        title="Image"
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setKind("poll")}
                        className={`p-1.5 rounded-full glass-hover ${
                          kind === "poll"
                            ? "text-[#FF6AD5]"
                            : "text-white/60"
                        }`}
                        title="Poll"
                      >
                        <BarChart3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setKind("announcement")}
                        className={`p-1.5 rounded-full glass-hover ${
                          kind === "announcement"
                            ? "text-[#FFD54A]"
                            : "text-white/60"
                        }`}
                        title="Announcement"
                      >
                        <Megaphone className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        onClick={reset}
                        className="h-8 px-2.5 text-xs text-white/60 hover:text-white"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={submit}
                        disabled={posting || !content.trim()}
                        data-testid="feed-post-btn"
                        className="rounded-full h-8 px-4 text-xs font-semibold text-black shrink-0"
                        style={{
                          background:
                            "linear-gradient(135deg,#FFBF00,#FF8A00)",
                        }}
                      >
                        <Send className="w-3 h-3 mr-1" /> Pluse
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Saved Pluse Full-Page Modal Overlay */}
            <AnimatePresence>
              {mode === "saved" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
                >
                  <div className="glass-strong rounded-3xl border border-white/10 w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                      <div className="flex items-center gap-2.5">
                        <Bookmark className="w-5 h-5 text-[#FFD54A] fill-[#FFD54A]" />
                        <h2 className="font-display font-bold text-lg text-white">
                          Saved Pluse ({savedPosts.length})
                        </h2>
                      </div>
                      <button
                        onClick={() => setMode(null)}
                        className="p-2 rounded-full glass glass-hover text-white/70 hover:text-white"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="p-6 overflow-y-auto space-y-6 flex-1">
                      {savedPosts.length === 0 ? (
                        <div className="text-center py-20 text-white/50 text-sm">
                          No saved posts yet. Bookmark any Pluse to view it
                          here.
                        </div>
                      ) : (
                        savedPosts.map((sp, idx) => (
                          <PostCard
                            key={sp.id}
                            post={sp}
                            delay={idx * 0.03}
                            onDeleted={(deletedId) => {
                              setSavedPosts((current) =>
                                current.filter((post) => post.id !== deletedId)
                              );

                              setPosts((current) =>
                                current.filter((post) => post.id !== deletedId)
                              );
                            }}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right Next to Profile: Horizontal Scrolling Trending Hashtags */}
          {trending.length > 0 && mode !== "composer" && mode !== "saved" && (
            <div
              className="glass rounded-2xl p-2.5 shadow-md flex items-center gap-2 flex-1 min-w-0"
              data-testid="trending-hashtags"
            >
              <TrendingUp className="w-3.5 h-3.5 text-[#6EE7FF] shrink-0" />
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
                {trending.slice(0, 3).map((t) => (
                  <Link
                    key={t.tag}
                    to={`/hashtag/${encodeURIComponent(t.tag)}`}
                    data-testid={`trending-${t.tag}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:border-[#6EE7FF]/40 hover:bg-[#6EE7FF]/10 transition-colors text-xs shrink-0 whitespace-nowrap"
                  >
                    <Hash className="w-3 h-3 text-[#6EE7FF]" />
                    <span className="font-medium">{t.tag}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {visiblePosts.map((p, i) => {
          const showSug =
            suggestions.length > 0 && (i + 1) % sugFreq === 0;
          const sug = showSug
            ? suggestions[
                Math.floor(i / sugFreq) % suggestions.length
              ]
            : null;
          return (
            <React.Fragment key={p.id}>
              <PostCard
                post={p}
                delay={i * 0.03}
                onDeleted={(deletedId) => {
                  setPosts((current) =>
                    current.filter((post) => post.id !== deletedId)
                  );
                }}
              />
              {sug && <SuggestionCard item={sug} delay={0.05} />}
              {(i + 1) % 5 === 0 && i < visiblePosts.length - 1 && (
                <div key={`pluse-infeed-${p.id}`} className="pluse-extra-ad">
                  <PluseAdSense type="inFeed" />
                </div>
              )}
              {(i + 1) % 15 === 0 && i < visiblePosts.length - 1 && (
                <div key={`pluse-inarticle-${p.id}`} className="pluse-extra-ad">
                  <PluseAdSense type="inArticle" />
                </div>
              )}
            </React.Fragment>
          );
        })}
        {visiblePosts.length > 0 && (
          <div className="pluse-extra-ad pluse-multiplex-ad">
            <PluseAdSense type="multiplex" />
          </div>
        )}
        {visiblePosts.length === 0 && (
          <div className="text-white/50 text-center py-16 text-sm">
            {hashtagTag ? `No posts found for #${decodeURIComponent(hashtagTag).replace(/^#/, "")}.` : "No pluse yet."}
          </div>
        )}
      </div>
    </div>
  );
}